import { useRef, useReducer } from 'react'
import Dropzone, { IDropzoneProps } from 'react-dropzone-uploader'
import { Typography, Stack, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ResponseMessage } from '../../shared/model/models'
import { fsColors } from '../../shared/constants'
import FsAlert, { FsAlertRef } from '../common/atoms/Alert'
import { ptAPI } from '../../shared/api/PTAPI'
import { ReactComponent as UploadIcon } from '../../assets/etl/cloud_icon.svg'
import { useAppSelector } from '../../store/hooks'

interface PtUploadProps {
  onSuccess?: () => void
  onError?: () => void
  setIsLoading: (isLoading: boolean) => void
  date: Date | null
}

const PtUpload = (props: PtUploadProps) => {
  const { t } = useTranslation()
  const { onSuccess, onError, setIsLoading, date } = props
  const theme = useTheme()
  const alertRef = useRef<FsAlertRef>(null)
  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  const { currentLanguage } = useAppSelector((state) => state.currentUser)

  const handleSubmit: IDropzoneProps['onSubmit'] = (files, allFiles) => {
    if (date) {
      setIsLoading(true)
      ptAPI
        .uploadFiles(
          allFiles.map((f) => f.file),
          date,
        )
        .then((res) => {
          if (res.status === 201) {
            alertRef.current?.openAlert({
              status: res,
              severity: 'success',
              message: t('file.upload.success'),
            } as ResponseMessage)
            allFiles.forEach((f) => f.remove())
            onSuccess?.()
          }
        })
        .catch((err) => {
          if (err.status === 413) {
            alertRef.current?.openAlert({
              status: err,
              severity: 'error',
              message: t('file.upload.toobig'),
            } as ResponseMessage)
            onError?.()
          } else {
            alertRef.current?.openAlert({
              status: err,
              severity: 'error',
              message: t('file.upload.error'),
            } as ResponseMessage)
            onError?.()
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  return (
    <div>
      <Typography variant="h6" mt={2}>
        {currentLanguage === 'de' ? 'Daten hochladen' : 'Upload your Data'}
      </Typography>
      <Typography>{t('upload.files.info')}</Typography>
      <Dropzone
        disabled={!date}
        onSubmit={handleSubmit}
        accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.csv"
        styles={{
          dropzone: {
            marginTop: 8,
            minHeight: 200,
            paddingLeft: 20,
            paddingRight: 20,
          },
          inputLabel: {
            padding: '16px 172px',
            color: theme.palette.primary.main,
            fontSize: 16,
            textAlign: 'center',
          },
          preview: {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.text.secondary
                : theme.palette.action.hover,
            marginTop: 16,
            borderRadius: 4,
            paddingTop: 4,
            paddingBottom: 4,
          },
          submitButton: {
            backgroundColor: theme.palette.primary.main,
            padding: '12px 24px',
            fontWeight: 'normal',
          },
          inputLabelWithFiles: {
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            padding: '8px 16px',
            marginLeft: '0px',
          },
        }}
        submitButtonContent={t('file.upload.submit')}
        inputWithFilesContent={t('add.more.files')}
        multiple
        inputContent={
          <Stack alignItems="center">
            <UploadIcon />
            <Typography variant="h6" textAlign="center" mt={2}>
              {t('file.drag.drop')}
            </Typography>
          </Stack>
        }
        onChangeStatus={(_, status) => {
          if (status === 'rejected_file_type') {
            alertRef.current?.openAlert({
              message: t('file.upload.toobig'),
              severity: 'error',
            })
          } else {
            forceUpdate()
          }
        }}
      />
      <FsAlert ref={alertRef} />
    </div>
  )
}

export default PtUpload
